/**
 * Custom Hook for Reviews Management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  reviewsService,
  Review,
  ReviewStats,
  ReviewsQuery,
  PaginatedReviews,
  CreateReviewDto,
  UpdateReviewDto,
} from '../services/reviews.service';

interface UseReviewsState {
  reviews: Review[];
  selectedReview: Review | null;
  total: number;
  averageRating: number;
  verifiedReviews: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  statsLoading: boolean;
  error: string | null;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  stats: ReviewStats | null;
}

export function useReviews(initialQuery?: ReviewsQuery) {
  const [state, setState] = useState<UseReviewsState>({
    reviews: [],
    selectedReview: null,
    total: 0,
    averageRating: 0,
    verifiedReviews: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    loading: true,
    statsLoading: true,
    error: null,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    stats: null,
  });

  const [query, setQuery] = useState<ReviewsQuery>(initialQuery || { page: 1, limit: 10 });

  const fetchStats = useCallback(async () => {
    setState((prev) => ({ ...prev, statsLoading: true }));
    try {
      const response = await reviewsService.getReviewStats();
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          stats: response.data!,
          verifiedReviews: response.data!.verifiedReviews,
          statsLoading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, statsLoading: false }));
      }
    } catch {
      setState((prev) => ({ ...prev, statsLoading: false }));
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await reviewsService.getReviews(query);
      if (response.success && response.data) {
        const raw = response.data as any;
        const reviews: Review[] = Array.isArray(raw) ? raw : (raw.data ?? []);
        const total: number = raw.total ?? reviews.length;
        const averageRating: number = raw.averageRating ?? 0;
        const page: number = raw.page ?? 1;
        const limit: number = raw.limit ?? 10;
        const totalPages: number = raw.totalPages ?? Math.ceil(total / (raw.limit ?? 10));
        const ratingDistribution = raw.ratingDistribution ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

        setState((prev) => ({
          ...prev,
          reviews,
          total,
          averageRating,
          page,
          limit,
          totalPages,
          ratingDistribution,
          loading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load reviews',
      }));
    }
  }, [query]);

  const fetchReview = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await reviewsService.getReviewById(id);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          selectedReview: response.data!,
          loading: false,
        }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load review',
      }));
    }
  }, []);

  const createReview = useCallback(async (data: CreateReviewDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await reviewsService.createReview(data);
      if (response.success) {
        await fetchReviews();
        await fetchStats();
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to create review',
      }));
      throw error;
    }
  }, [fetchReviews, fetchStats]);

  const updateReview = useCallback(async (id: string, data: UpdateReviewDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await reviewsService.updateReview(id, data);
      if (response.success) {
        await fetchReviews();
        await fetchStats();
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update review',
      }));
      throw error;
    }
  }, [fetchReviews, fetchStats]);

  const deleteReview = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await reviewsService.deleteReview(id);
      await fetchReviews();
      await fetchStats();
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to delete review',
      }));
      throw error;
    }
  }, [fetchReviews, fetchStats]);

  const updateQuery = useCallback((newQuery: Partial<ReviewsQuery>) => {
    setQuery((prev) => ({ ...prev, ...newQuery }));
  }, []);

  const refresh = useCallback(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews, fetchStats]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    reviews: state.reviews,
    selectedReview: state.selectedReview,
    total: state.total,
    averageRating: state.averageRating,
    verifiedReviews: state.verifiedReviews,
    page: state.page,
    limit: state.limit,
    totalPages: state.totalPages,
    loading: state.loading,
    statsLoading: state.statsLoading,
    error: state.error,
    ratingDistribution: state.ratingDistribution,
    stats: state.stats,
    query,
    fetchReview,
    createReview,
    updateReview,
    deleteReview,
    updateQuery,
    refresh,
  };
}